import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DEFAULT_PAGINATION_PARAMS, PaginatedResponse, PaginationParams } from '../../../core/shared/models/pagination.model';
import { environment } from '../../../../environments/environment';
import { ParticipanteDomainModel } from '../models/participante.model';
import { ParticipantesFiltro } from '../models/participantes-filtro.model';
import { ParticipanteDetalhamento } from '../models/participante-detalhamento.model';
import { ParticipanteRepository } from '../repositories/participante.repository';
import { FinalizarEtapaResponse } from '../models/finalizar-etapa-response.model';
import { ReabrirEtapaResponse } from '../models/reabrir-etapa-response.model';
import { ProrrogarEtapaResponse } from '../models/prorrogar-etapa-response.model';
import {
  AceitarProcessoResponse,
  BusResponse,
  CancelarProcessoResponse,
  EstadoProcessoResponse,
  GerarProcessoRequest,
  NovaPropostaResponse,
  VerificarElegibilidadeResponse,
  VerificarMatriculasResponse
} from '../models/processo-manual.model';
import {
  ConsultarPerguntasPropostaResponse,
  PerguntasPropostaRequest,
  PerguntasPropostaResponse
} from '../models/proposta-manual.model';
import { VendedorProposta } from '../../proposta-financiamento/models/dados-proposta.model';

@Injectable({
  providedIn: 'root'
})
export class ParticipanteService implements ParticipanteRepository {
  private readonly apiParticipante = 'participante/';

  constructor(private readonly http: HttpClient) {}

  listarTodos(
    filtros?: ParticipantesFiltro,
    pagination: PaginationParams = DEFAULT_PAGINATION_PARAMS
  ): Observable<PaginatedResponse<ParticipanteDomainModel>> {
    let params = new HttpParams()
      .set('page', pagination.page)
      .set('size', pagination.size);
    if (filtros?.status) params = params.set('status', filtros.status);
    if (filtros?.etapas?.length) params = params.set('etapas', filtros.etapas.join(','));
    if (filtros?.responsavel) params = params.set('responsavel', filtros.responsavel);
    if (filtros?.filtro) params = params.set('filtro', filtros.filtro);
    return this.http.get<PaginatedResponse<ParticipanteDomainModel>>(
      `${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}listarTodos`,
      { params }
    );
  }

  listarCabecalho(matricula: number): Observable<ParticipanteDetalhamento> {
    return this.http.get<ParticipanteDetalhamento>(`${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}listarCabecalho/${matricula}`);
  }

  estadoProcesso(cpf: string): Observable<EstadoProcessoResponse> {
    return this.http.get<EstadoProcessoResponse>(`${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}estadoProcesso/${cpf}`);
  }

  verificarElegibilidade(cpf: string | null, matricula?: number | null): Observable<VerificarElegibilidadeResponse> {
    let params = new HttpParams();
    if (cpf) params = params.set('cpf', cpf);
    if (matricula != null) params = params.set('matricula', matricula.toString());
    return this.http.get<VerificarElegibilidadeResponse>(`${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}verificarElegibilidade`, { params });
  }

  gerarProcesso(request: GerarProcessoRequest): Observable<BusResponse<NovaPropostaResponse>> {
    return this.http.post<BusResponse<NovaPropostaResponse>>(`${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}gerarProcesso`, request);
  }

  aceitarProcesso(proposta: number): Observable<AceitarProcessoResponse> {
    return this.http.put<AceitarProcessoResponse>(`${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}aceitarProcesso/${proposta}`, {});
  }

  cancelarProcesso(proposta: number, condominio: number | null): Observable<CancelarProcessoResponse> {
    let params = new HttpParams().set('proposta', proposta.toString());
    if (condominio != null) {
      params = params.set('condominio', condominio.toString());
    }

    return this.http.put<CancelarProcessoResponse>(`${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}cancelarProcesso`, {}, { params });
  }

  finalizarEtapa(idProposta: string, idEtapa: string): Observable<FinalizarEtapaResponse> {
    return this.http.put<FinalizarEtapaResponse>(`${environment.FI_FINANC_IMOB_BE}evento/proposta/${idProposta}/etapa/${idEtapa}/finalizarEtapa`, {});
  }

  reabrirEtapa(idProposta: string, idEtapa: string, justificativa?: string): Observable<ReabrirEtapaResponse> {
    const body = justificativa ? { justificativa } : {};
    return this.http.put<ReabrirEtapaResponse>(`${environment.FI_FINANC_IMOB_BE}evento/proposta/${idProposta}/etapa/${idEtapa}/reabrirEtapa`, body);
  }

  prorrogarEtapa(idProcesso: string, idEtapa: string, qtdDiasProrrogados: number, mensagemJustificativa: string): Observable<ProrrogarEtapaResponse> {
    const body = {
      mensagemJustificativa,
      qtdDiasProrrogados
    };
    return this.http.post<ProrrogarEtapaResponse>(`${environment.FI_FINANC_IMOB_BE}evento/processo/${idProcesso}/etapa/${idEtapa}/prorrogarEtapa`, body);
  }

  verificarMatriculas(cpf: string): Observable<VerificarMatriculasResponse> {
    return this.http.get<VerificarMatriculasResponse>(`${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}verificarMatriculas/${cpf}`);
  }

  enviarPerguntasPropostaManual(proposta: number, request: PerguntasPropostaRequest): Observable<PerguntasPropostaResponse> {
    return this.http.put<PerguntasPropostaResponse>(
      `${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}propostaManual/perguntasProposta/${proposta}`,
      request
    );
  }

  consultarPerguntasPropostaManual(proposta: number): Observable<ConsultarPerguntasPropostaResponse> {
    return this.http.get<ConsultarPerguntasPropostaResponse>(
      `${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}propostaManual/perguntasProposta/estadoAtual/${proposta}`
    );
  }

  recuperarDadosVendedorPropostaManual(cpf: string): Observable<VendedorProposta> {
    return this.http.get<VendedorProposta>(
      `${environment.FI_FINANC_IMOB_BE}${this.apiParticipante}propostaManual/dadosVendedor/${cpf}`
    );
  }

}


